import { Search as SearchIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  InputAdornment,
  Checkbox,
  Radio,
  Typography,
} from '@mui/material';
import {
  DataGridProps,
  getGridStringOperators,
  GridFilterOperator,
  GridSortModel,
} from '@mui/x-data-grid';
import {
  AdvancedFilterModel,
  PaginatedDataGrid,
  gridFilterModelToAdvancedFilterModel,
} from '@nohara-dxdev/react-core';
import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useController,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useTranslatedFilterOperators } from '@/hooks';
import { ItemEntity } from '@/types/api';

import { useItems } from '../api/get-items';
import {
  itemSortableFields,
  ItemSortableField,
  ItemSortOrder,
  useItemFilterValues,
} from '../providers';

interface SelectItemModalProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  rules?: RegisterOptions<TFieldValues, TName>;
  open: boolean;
  onClose: () => void;
  multiValue?: boolean;
  maxItems?: number;
  title?: string;
  /** When true, appends a Status column showing the translated item status */
  showStatusColumn?: boolean;
  /** When provided, rows for which this returns false have their selection control disabled and cannot be selected */
  isRowSelectable?: (row: ItemEntity) => boolean;
  onSelect?: (items: ItemEntity[] | ItemEntity | null) => void;
  onCancel?: () => void;
}

type SelectItemModalComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: SelectItemModalProps<TFieldValues, TName>,
) => React.JSX.Element;

export const SelectItemModal: SelectItemModalComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  rules,
  open,
  onClose,
  multiValue = false,
  maxItems,
  title,
  showStatusColumn = false,
  isRowSelectable,
  onSelect,
  onCancel,
}: SelectItemModalProps<TFieldValues, TName>) => {
  const { t } = useTranslation();
  const form = useFormContext<TFieldValues>();

  // Form integration using react-hook-form
  const {
    field: { value, onChange },
    fieldState: { error: fieldError },
    formState: { disabled },
  } = useController({
    name,
    control: form.control,
    rules,
  });

  // Get filter values from context
  const filterValues = useItemFilterValues();

  // Local state for search and selection
  const [searchValue, setSearchValue] = useState('');
  const [selectedItems, setSelectedItems] = useState<ItemEntity[]>([]);

  // Server-side sorting state
  const [sortField, setSortField] = useState<ItemSortableField | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<ItemSortOrder | undefined>(undefined);

  // Advanced filter state (for filter panel)
  const [pendingAdvancedFilterModel, setPendingAdvancedFilterModel] = useState<AdvancedFilterModel>(
    {
      conditions: [],
    },
  );
  const [appliedAdvancedFilterModel, setAppliedAdvancedFilterModel] = useState<AdvancedFilterModel>(
    {
      conditions: [],
    },
  );

  useEffect(() => {
    if (!value) {
      setSelectedItems([]);
      return;
    }

    if (multiValue) {
      setSelectedItems(Array.isArray(value) ? value : [value]);
    } else {
      setSelectedItems(value ? [value] : []);
    }
  }, [value, multiValue]);

  // Build advancedFilterModel from context filter values if they exist
  const combinedAdvancedFilterModel = useMemo<AdvancedFilterModel | undefined>(() => {
    // Use applied filter model if it exists (from filter panel)
    if (appliedAdvancedFilterModel.conditions.length > 0) {
      return appliedAdvancedFilterModel;
    }
    // If context already provides advancedFilterModel, use it
    if (
      filterValues.advancedFilterModel &&
      filterValues.advancedFilterModel.conditions.length > 0
    ) {
      return filterValues.advancedFilterModel;
    }
    // If context provides filterModel, convert it to advancedFilterModel format
    if (filterValues.filterModel && filterValues.filterModel.length > 0) {
      // Convert each filterModel to advancedFilterModel and combine them
      const allConditions = filterValues.filterModel.flatMap((model, modelIndex) => {
        const advancedModel = gridFilterModelToAdvancedFilterModel({
          items: model.items.map((item) => ({
            field: item.field,
            operator: item.operator,
            value: item.value ?? '',
          })),
          logicOperator: model.logicOperator,
        });
        // Add AND logic operator between different filterModels (except the first condition of the first model)
        return advancedModel.conditions.map((condition, conditionIndex) => ({
          ...condition,
          logicOperator:
            modelIndex === 0 && conditionIndex === 0 ? undefined : ('and' as 'and' | 'or'),
        }));
      });
      return allConditions.length > 0 ? { conditions: allConditions } : undefined;
    }
    return undefined;
  }, [filterValues.advancedFilterModel, filterValues.filterModel, appliedAdvancedFilterModel]);

  // Combine search value with context filter values
  const effectiveNameOrItemNumberMatch = searchValue || filterValues.nameOrItemNumberMatch;

  // Convert state to MUI DataGrid sortModel format
  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortField) {
      return [];
    }
    return [{ field: sortField, sort: sortOrder }];
  }, [sortField, sortOrder]);

  // Handle sort model changes from DataGrid
  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length === 0) {
      // Reset to undefined when sorting is cleared
      setSortField(undefined);
      setSortOrder(undefined);
      return;
    }

    const { field, sort } = model[0];

    // Only allow sorting on fields that exist in the backend
    if (itemSortableFields.includes(field as ItemSortableField)) {
      setSortField(field as ItemSortableField);
      setSortOrder((sort ?? 'asc') as ItemSortOrder);
    }
  }, []);

  // Load items with combined filters and sorting
  const itemsQuery = useItems({
    nameOrItemNumberMatch: effectiveNameOrItemNumberMatch,
    advancedFilterModel:
      combinedAdvancedFilterModel && combinedAdvancedFilterModel.conditions.length > 0
        ? combinedAdvancedFilterModel
        : undefined,
    excludeIds: filterValues.excludeIds,
    locationIdOrAdjacentLocationIdEq: filterValues.locationIdOrAdjacentLocationIdEq,
    infrastructureRelationshipRequirements: filterValues.infrastructureRelationshipRequirements,
    sortField,
    sortOrder,
    relationships: [
      'itemType',
      'constructionType',
      'circuits',
      'itemTemplate',
      'building',
      'floor',
      'locationEntity',
      'systems',
    ],
    queryConfig: {
      enabled: open, // Only fetch when modal is open
    },
  });

  const { isLoading } = itemsQuery;

  // All selectable items on the current page (used for header checkbox in multi-select mode)
  const selectableItems = useMemo(() => {
    const rows = Array.isArray(itemsQuery.data) ? itemsQuery.data : [];
    return isRowSelectable ? rows.filter(isRowSelectable) : rows;
  }, [itemsQuery.data, isRowSelectable]);

  const allPageSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedItems.some((s) => s.id === item.id));
  const somePageSelected =
    !allPageSelected && selectableItems.some((item) => selectedItems.some((s) => s.id === item.id));

  const handleSelectAll = useCallback(() => {
    if (allPageSelected) {
      setSelectedItems((prev) =>
        prev.filter((selected) => !selectableItems.some((item) => item.id === selected.id)),
      );
    } else {
      setSelectedItems((prev) => {
        const newItems = selectableItems.filter((item) => !prev.some((s) => s.id === item.id));
        if (maxItems !== undefined) {
          const available = Math.max(0, maxItems - prev.length);
          return [...prev, ...newItems.slice(0, available)];
        }
        return [...prev, ...newItems];
      });
    }
  }, [allPageSelected, selectableItems, maxItems]);

  // Handle search input change
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  // Handle item selection
  const handleItemSelect = useCallback(
    (item: ItemEntity) => {
      // Skip non-selectable rows
      if (isRowSelectable && !isRowSelectable(item)) {
        return;
      }
      if (multiValue) {
        setSelectedItems((prev) => {
          const isSelected = prev.some((selectedItem) => selectedItem.id === item.id);
          if (isSelected) {
            return prev.filter((selectedItem) => selectedItem.id !== item.id);
          } else {
            if (maxItems !== undefined && prev.length >= maxItems) {
              return prev;
            }
            return [...prev, item];
          }
        });
      } else {
        setSelectedItems([item]);
      }
    },
    [multiValue, maxItems, isRowSelectable],
  );

  // Handle select button click
  const handleSelect = useCallback(() => {
    if (multiValue) {
      onChange(selectedItems);
      onSelect?.(selectedItems);
    } else {
      onChange(selectedItems[0] || null);
      onSelect?.(selectedItems[0] || null);
    }
    onClose();
  }, [selectedItems, multiValue, onChange, onSelect, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Reset selection to current form value
    if (!value) {
      setSelectedItems([]);
    } else {
      // Handle both single value (ItemEntity) and multi-value (ItemEntity[]) cases
      if (multiValue) {
        setSelectedItems(Array.isArray(value) ? value : [value]);
      } else {
        setSelectedItems([value]);
      }
    }
    setSearchValue('');
    // Reset filters to initial state
    const emptyFilterModel: AdvancedFilterModel = { conditions: [] };
    setPendingAdvancedFilterModel(emptyFilterModel);
    setAppliedAdvancedFilterModel(emptyFilterModel);
    // Reset sorting
    setSortField(undefined);
    setSortOrder(undefined);
    onCancel?.();
    onClose();
  }, [value, multiValue, onCancel, onClose]);

  // Handle advanced filter model changes from filter panel (only updates pending filter, not applied)
  const handleAdvancedFilterModelChange = useCallback((model: AdvancedFilterModel) => {
    setPendingAdvancedFilterModel(model);
  }, []);

  // Apply the pending advanced filter model (called from filter panel)
  const handleAdvancedFilterApply = useCallback((model: AdvancedFilterModel) => {
    setAppliedAdvancedFilterModel(model);
    setPendingAdvancedFilterModel(model);
  }, []);

  // Clear all filters (called from filter panel)
  const handleAdvancedFilterClear = useCallback(() => {
    const emptyFilterModel: AdvancedFilterModel = { conditions: [] };
    setPendingAdvancedFilterModel(emptyFilterModel);
    setAppliedAdvancedFilterModel(emptyFilterModel);
  }, []);

  // Filter operators for text columns (backend supports: contains, startsWith, endsWith, isEmpty, isNotEmpty)
  const textFilterOperators = useTranslatedFilterOperators(getGridStringOperators(), [
    'contains',
    'startsWith',
    'endsWith',
    'isEmpty',
    'isNotEmpty',
  ]) as GridFilterOperator<ItemEntity, string, unknown>[];

  // Define table columns
  const columns: DataGridProps<ItemEntity>['columns'] = useMemo(() => {
    const baseColumns: NonNullable<DataGridProps<ItemEntity>['columns']>[number][] = [
      {
        field: 'selection',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        renderHeader: multiValue
          ? () => (
              <Checkbox
                checked={allPageSelected}
                indeterminate={somePageSelected}
                onChange={handleSelectAll}
                disabled={disabled || isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            )
          : undefined,
        renderCell: (params) => {
          const isSelected = selectedItems.some((item) => item.id === params.row.id);
          const selectable = isRowSelectable ? isRowSelectable(params.row) : true;
          const isDisabled = disabled || !selectable;

          if (multiValue) {
            return (
              <Checkbox
                checked={isSelected}
                onChange={() => {
                  handleItemSelect(params.row);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                disabled={isDisabled}
              />
            );
          } else {
            return (
              <Radio
                checked={isSelected}
                onChange={() => {
                  handleItemSelect(params.row);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                disabled={isDisabled}
              />
            );
          }
        },
      },
      {
        field: 'name',
        headerName: t('items.table.name'),
        flex: 1,
        minWidth: 200,
        sortable: true,
        filterable: true,
        filterOperators: textFilterOperators,
      },
      {
        field: 'itemNumber',
        headerName: t('items.table.number'),
        width: 120,
        sortable: true,
        filterable: true,
        filterOperators: textFilterOperators,
        valueGetter: (_, row) => row.itemNumber,
      },
      {
        field: 'itemType',
        headerName: t('items.table.itemType'),
        width: 150,
        filterable: false,
        renderCell: (params) => params.row.itemType?.value ?? '-',
      },
      {
        field: 'locationEntity.name',
        headerName: t('reports.columns.room', 'Room'),
        width: 200,
        filterable: true,
        filterOperators: textFilterOperators,
        valueGetter: (_, row) => {
          const parts: string[] = [];
          if (row.building?.name) parts.push(row.building.name);
          if (row.floor?.name) parts.push(row.floor.name);
          if (row.locationEntity?.name) parts.push(row.locationEntity.name);
          return parts.length > 0 ? parts.join(' / ') : '-';
        },
      },
      {
        field: 'systems.name',
        headerName: t('reports.columns.system', 'System'),
        width: 200,
        filterable: true,
        filterOperators: textFilterOperators,
        valueGetter: (_, row) => {
          const parts: string[] = [];
          if (row.constructionType?.value) parts.push(row.constructionType.value);
          if (row.systems?.length) {
            for (const system of row.systems) {
              parts.push(system.name);
            }
          }
          return parts.length > 0 ? parts.join(' / ') : '-';
        },
      },
    ];

    if (showStatusColumn) {
      baseColumns.push({
        field: 'status',
        headerName: t('items.selectModal.statusColumn'),
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (params: { row: ItemEntity }) =>
          t(`items.status.${params.row.status}`, params.row.status),
      });
    }

    return baseColumns;
  }, [
    selectedItems,
    multiValue,
    handleItemSelect,
    handleSelectAll,
    allPageSelected,
    somePageSelected,
    disabled,
    isLoading,
    t,
    textFilterOperators,
    showStatusColumn,
    isRowSelectable,
  ]);

  const modalTitle =
    title ??
    (multiValue
      ? t('common.selectModal.titleMultiple', { type: t('items.title') })
      : t('common.selectModal.titleSingle', { type: t('items.title') }));

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: { sx: { height: '80vh', maxHeight: 800 } },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">{modalTitle}</Typography>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder={t('items.selectModal.searchPlaceholder')}
          value={searchValue}
          onChange={handleSearchChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          disabled={disabled}
        />

        {/* Items Table */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <PaginatedDataGrid
            paginatedQuery={itemsQuery}
            columns={columns}
            showTopPagination
            loading={isLoading}
            getRowId={(row) => row.id}
            hideFooterSelectedRowCount
            pagination
            enableAdvancedFilters={true}
            advancedFilterModel={pendingAdvancedFilterModel}
            onAdvancedFilterModelChange={handleAdvancedFilterModelChange}
            onAdvancedFilterApply={handleAdvancedFilterApply}
            onAdvancedFilterClear={handleAdvancedFilterClear}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            sortingMode="server"
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              '& .MuiDataGrid-row.row-non-selectable': {
                opacity: 0.5,
                cursor: 'default',
              },
            }}
            getRowClassName={(params) => {
              if (isRowSelectable && !isRowSelectable(params.row)) {
                return 'row-non-selectable';
              }
              return '';
            }}
            onRowClick={(params) => {
              handleItemSelect(params.row as ItemEntity);
            }}
            slotProps={{
              noRowsOverlay: {
                title:
                  effectiveNameOrItemNumberMatch || appliedAdvancedFilterModel.conditions.length > 0
                    ? t('items.selectModal.noSearchResults')
                    : t('items.selectModal.noItems'),
              },
            }}
          />
        </Box>

        {/* Selection Summary */}
        {selectedItems.length > 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {multiValue
                ? t('common.selectModal.selectedMultiple', {
                    type: t('items.title'),
                    count: selectedItems.length,
                  })
                : t('common.selectModal.selectedSingle', {
                    type: t('items.title'),
                    name: selectedItems[0]?.name || '',
                  })}
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {fieldError && (
          <Typography variant="body2" color="error">
            {fieldError.message}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} disabled={disabled}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          disabled={disabled || selectedItems.length === 0}
        >
          {t('common.select')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
